import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';

type SectorRow = {
  industry?: string;
};

export type OrgFrameworkPreviewContext = {
  industry: string;
  strategic_orientation: string;
};

/** Maps clarify chip keys to routing keywords (aligned with server framework keywords). */
export const CLARIFY_CHIP_SIGNALS: Record<string, string> = {
  chipRevenueDecline: 'revenue decline growth margin sales',
  chipCostPressure: 'cost margin budget procurement',
  chipOperationalInefficiency: 'operations operational efficiency bottleneck process',
  chipLeadership: 'leadership management organizational culture transformation',
  chipMarket: 'market competitive global expansion customer',
  chipOther: '',
};

function industryFromSector(sector: unknown): string {
  if (!sector || typeof sector !== 'object') return '';
  return (sector as SectorRow).industry?.trim() ?? '';
}

/** Industry + orientation saved via onboarding (`save-context` → `strategic_contexts`). */
const useOrgFrameworkPreviewContext = () => {
  const { userProfile } = useAuthContext();
  const orgId = userProfile?.org_id;

  return useQuery({
    queryKey: ['strategic_contexts', 'framework-preview-inputs', orgId],
    queryFn: async (): Promise<OrgFrameworkPreviewContext> => {
      const { data, error } = await supabaseClient
        .from('strategic_contexts')
        .select('sector, strategic_orientation')
        .eq('org_id', orgId!)
        .maybeSingle();

      if (error) throw new Error(error.message);

      return {
        industry: industryFromSector(data?.sector),
        strategic_orientation: data?.strategic_orientation?.trim() ?? '',
      };
    },
    enabled: !!orgId,
  });
};

export default useOrgFrameworkPreviewContext;

/** Full case text for `framework-preview` `issue_signals` (substring keyword matching on server). */
export function buildFrameworkCaseText(...parts: (string | string[] | undefined | null)[]): string {
  const tokens = parts.flatMap((part) => {
    if (part == null) return [];
    if (Array.isArray(part)) return part;
    return [part];
  });

  return tokens
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildClarifyCaseText(
  context: { issue_title?: string; short_description?: string },
  form: {
    core_constraints: string[];
    core_constraint_detail?: string;
    decision_needed?: string;
  },
): string {
  const chipText = form.core_constraints
    .map((chip) => CLARIFY_CHIP_SIGNALS[chip] ?? '')
    .join(' ');

  return buildFrameworkCaseText(
    context.issue_title,
    context.short_description,
    chipText,
    form.core_constraint_detail,
    form.decision_needed,
  );
}

/** @deprecated Use buildFrameworkCaseText */
export const buildFrameworkIssueSignals = buildFrameworkCaseText;
